import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { api } from "../App";
import { styledToast as toast } from "@/components/ui/sonner";
import { useTranslation } from 'react-i18next';
import { 
  FileText, 
  CheckCircle, 
  ArrowRight,
  Filter,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Calendar,
  X,
  UserCheck,
} from "lucide-react";
import { LoadingOverlay } from "./LoadingOverlay";
import {
  AssignOfficialModal,
  SURVEY_WORKFLOW_STATUSES,
  REVENUE_WORKFLOW_STATUSES,
  getDesignationLabel,
  getLocationInfo,
} from "./AssignOfficialModal";

// Kannada to English Hobli mapping
const HOBLI_KANNADA_TO_ENGLISH = {
  "ಕಸಬಾ": "Kasaba",
  "ಕುದೂರು": "Kudur",
  "ಮಾಡಬಾಳು": "Madabalu",
  "ತಿಪ್ಪಸಂದ್ರ": "Tippasandra",
  "ಮಂಚನಬೆಲೆ": "Manchanabele",
};

// Kannada to English Circle mapping - Comprehensive list from database
const CIRCLE_KANNADA_TO_ENGLISH = {
  // All circles from database
  "ಅಜ್ಜನಹಳ್ಳಿ": "Ajjanahalli",
  "ಅಜ್ಜಹಳ್ಳಿ": "Ajjahalli",
  "ಅದರಂಗಿ": "Adarangi",
  "ಆಗಲಕೋಟೆ": "Agalakote",
  "ಕಣನೂರು": "Kananuru",
  "ಕರಳಮಂಗಲ": "Karalamangala",
  "ಕಲ್ಯಾ": "Kalya",
  "ಕಲ್ಲೂರು": "Kalluru",
  "ಕಾಳಾರಿಕಾವಲ್": "Kalari Kaval",
  "ಕಾಳಾರಿಕಾವಲ್‌": "Kalari Kaval",
  "ಕುದೂರು": "Kudur",
  "ಕೋಂಡಹಳ್ಳಿ": "Konda Halli",
  "ಕೊಂಡಹಳ್ಳಿ": "Konda Halli",
  "ಗಜ್ಜಗರಗುಪ್ಪೆ": "Gajjagaragguppe",
  "ಗೊಲ್ಲಹಳ್ಳಿ": "Gollahalli",
  "ಚಕ್ರಭಾವಿ": "Chakrabhavi",
  "ಚಿಕ್ಕಮುದಿಗೆರೆ": "Chikkamudigere",
  "ಚಿಕ್ಕಹಳ್ಳಿ": "Chikkahalli",
  "ತಗ್ಗೀಕುಪ್ಪೆ": "Taggikuppe",
  "ತಿಪ್ಪಸಂದ್ರ": "Tippasandra",
  "ದಬ್ಬಗುಳಿ": "Dabbaguli",
  "ದುಬ್ಬಗೊಟ್ಟಿಗೆ": "Dubbagottige",
  "ದೊಡ್ಡಸೋಮನಹಳ್ಳಿ": "Doddasomanahalli",
  "ನಾರಸಂದ್ರ": "Narasandra",
  "ನೇತೇನಹಳ್ಳಿ": "Nethenahalli",
  "ನೇರಳೆಕೆರೆ": "Neralekere",
  "ಬಗಿನಗೆರೆ": "Baginagere",
  "ಬಾಚೇನಹಟ್ಟಿ": "Bachenahatti",
  "ಬಿಸ್ಕೂರು": "Biskur",
  "ಬೆಟ್ಟಹಳ್ಳಿ": "Bettahalli",
  "ಬೆಳಗಾವಡಿ": "Belagavadi",
  "ಬ್ಯಾಲದಕೆರೆ": "Byaladakere",
  "ಮಟ್ಟಿಕೆರೆ": "Mattikere",
  "ಮಣ್ಣಿಗನಹಳ್ಳಿ": "Manniganahalli",
  "ಮರೂರು": "Maruru",
  "ಮಲ್ಲಸಂದ್ರ": "Mallasandra",
  "ಮಾಡಬಾಳು": "Madabalu",
  "ಮಾದಿಗೊಂಡನಹಳ್ಳಿ": "Madigondanahalli",
  "ಮಾಯನಾಯಕನಹಳ್ಳಿ": "Mayanayakanahalli",
  "ಮಾಯನಾಯ್ಕನಹಳ್ಳಿ": "Mayanayakanahalli",
  "ಮಾಯಸಂದ್ರ": "Mayasandra",
  "ಮಾರಸಂದ್ರ": "Marasandra",
  "ವಿಶ್ವನಾಥಪುರ": "Vishwanathapura",
  "ಶ್ರೀಪತಿಹಳ್ಳಿ": "Shreepathihalli",
  "ಸಂಕೀಘಟ್ಟ": "Sankighatta",
  "ಸಾತನೂರು": "Satanuru",
  "ಸೀಗೆಕುಪ್ಪೆ": "Seegekuppe",
  "ಹಂಚಿಕುಪ್ಪೆ": "Hanchikuppe",
  "ಹರ್ತಿ": "Harthi",
  "ಹುಲಿಕಟ್ಟೆ": "Hulikatte",
  "ಹುಲಿಕಲ್ಲು": "Hulikallu",
  "ಹುಳ್ಳೇನಹಳ್ಳಿ": "Hullenahalli",
  "ಹೆಬ್ಬಳಲು": "Hebbalalu",
};

// Hobli options
const HOBLIS = [
  { value: "all", labelKey: "hobli.all" },
  { value: "Kasaba", labelKey: "hobli.kasaba" },
  { value: "Kudur", labelKey: "hobli.kudur" },
  { value: "Madabalu", labelKey: "hobli.madabalu" },
  { value: "Tippasandra", labelKey: "hobli.tippasandra" },
];

// Circles by Hobli mapping (with Kannada labels)
const CIRCLES_BY_HOBLI = {
  Kasaba: [
    { value: "bachenahatti", label: "Bachenahatti", label_kn: "ಬಾಚೇನಹಟ್ಟಿ" },
    { value: "kalya", label: "Kalya", label_kn: "ಕಲ್ಯಾ" },
    { value: "shreepathi_halli", label: "Shreepathi Halli", label_kn: "ಶ್ರೀಪತಿಹಳ್ಳಿ" },
    { value: "kalari_kaval", label: "Kalari Kaval", label_kn: "ಕಾಳಾರಿಕಾವಲ್" },
    { value: "harthi", label: "Harthi", label_kn: "ಹರ್ತಿ" },
    { value: "konda_halli", label: "Konda Halli", label_kn: "ಕೊಂಡಹಳ್ಳಿ" },
    { value: "vishwanathapura", label: "Vishwanathapura", label_kn: "ವಿಶ್ವನಾಥಪುರ" },
    { value: "mayanayakana_halli", label: "Mayanayakana Halli", label_kn: "ಮಾಯನಾಯಕನಹಳ್ಳಿ" },
    { value: "kalluru", label: "Kalluru", label_kn: "ಕಲ್ಲೂರು" },
    { value: "agalakote", label: "Agalakote", label_kn: "ಆಗಲಕೋಟೆ" },
    { value: "taggikuppe", label: "Taggikuppe", label_kn: "ತಗ್ಗೀಕುಪ್ಪೆ" },
    { value: "gubbagottige", label: "Gubbagottige", label_kn: "ಗುಬ್ಬಗೊಟ್ಟಿಗೆ" },
    { value: "magadi", label: "Magadi", label_kn: "ಮಾಗಡಿ" },
  ],
  Kudur: [
    { value: "kudur", label: "Kudur", label_kn: "ಕುದೂರು" },
    { value: "ajjahalli", label: "Ajjahalli", label_kn: "ಅಜ್ಜಹಳ್ಳಿ" },
    { value: "adarangi", label: "Adarangi", label_kn: "ಅದರಂಗಿ" },
    { value: "kananuru", label: "Kananuru", label_kn: "ಕಣನೂರು" },
    { value: "gollahalli", label: "Gollahalli", label_kn: "ಗೊಲ್ಲಹಳ್ಳಿ" },
    { value: "narasandra", label: "Narasandra", label_kn: "ನಾರಸಂದ್ರ" },
    { value: "biskur", label: "Biskur", label_kn: "ಬಿಸ್ಕೂರು" },
    { value: "bettahalli", label: "Bettahalli", label_kn: "ಬೆಟ್ಟಹಳ್ಳಿ" },
    { value: "manniganahalli", label: "Manniganahalli", label_kn: "ಮಣ್ಣಿಗನಹಳ್ಳಿ" },
    { value: "maruru", label: "Maruru", label_kn: "ಮರೂರು" },
    { value: "madigondanahalli", label: "Madigondanahalli", label_kn: "ಮಾದಿಗೊಂಡನಹಳ್ಳಿ" },
    { value: "marasandra", label: "Marasandra", label_kn: "ಮಾರಸಂದ್ರ" },
    { value: "hulikallu", label: "Hulikallu", label_kn: "ಹುಲಿಕಲ್ಲು" },
  ],
  Tippasandra: [
    { value: "tippasandra", label: "Tippasandra", label_kn: "ತಿಪ್ಪಸಂದ್ರ" },
    { value: "chikka_mudigere", label: "Chikka Mudigere", label_kn: "ಚಿಕ್ಕಮುದಿಗೆರೆ" },
    { value: "chikkahalli", label: "Chikkahalli", label_kn: "ಚಿಕ್ಕಹಳ್ಳಿ" },
    { value: "dodda_somanahalli", label: "Dodda Somanahalli", label_kn: "ದೊಡ್ಡಸೋಮನಹಳ್ಳಿ" },
    { value: "neralekere", label: "Neralekere", label_kn: "ನೇರಳೆಕೆರೆ" },
    { value: "baginagere", label: "Baginagere", label_kn: "ಬಗಿನಗೆರೆ" },
    { value: "mallasandra", label: "Mallasandra", label_kn: "ಮಲ್ಲಸಂದ್ರ" },
    { value: "mayasandra", label: "Mayasandra", label_kn: "ಮಾಯಸಂದ್ರ" },
    { value: "sanki_ghatta", label: "Sankighatta", label_kn: "ಸಂಕೀಘಟ್ಟ" },
    { value: "hullena_halli", label: "Hullenahalli", label_kn: "ಹುಳ್ಳೇನಹಳ್ಳಿ" },
    { value: "hebbalalu", label: "Hebbalalu", label_kn: "ಹೆಬ್ಬಳಲು" },
  ],
  Madabalu: [
    { value: "madabalu", label: "Madabalu", label_kn: "ಮಾಡಬಾಳು" },
    { value: "ajjanahalli", label: "Ajjanahalli", label_kn: "ಅಜ್ಜನಹಳ್ಳಿ" },
    { value: "karalamangala", label: "Karalamangala", label_kn: "ಕರಳಮಂಗಲ" },
    { value: "gajjagaraguppe", label: "Gajjagaraguppe", label_kn: "ಗಜ್ಜಗರಗುಪ್ಪೆ" },
    { value: "chakrabhavi", label: "Chakrabhavi", label_kn: "ಚಕ್ರಭಾವಿ" },
    { value: "dabbaguli", label: "Dabbaguli", label_kn: "ದಬ್ಬಗುಳಿ" },
    { value: "mattikere", label: "Mattikere", label_kn: "ಮಟ್ಟಿಕೆರೆ" },
    { value: "seegekuppe", label: "Seegekuppe", label_kn: "ಸೀಗೆಕುಪ್ಪೆ" },
    { value: "hanchikuppe", label: "Hanchikuppe", label_kn: "ಹಂಚಿಕುಪ್ಪೆ" },
    { value: "hulikatte", label: "Hulikatte", label_kn: "ಹುಲಿಕಟ್ಟೆ" },
    { value: "belagavadi", label: "Belagavadi", label_kn: "ಬೆಳಗಾವಡಿ" },
  ],
};

// Designations eligible for bulk assignment
const BULK_ASSIGN_DESIGNATIONS = [
  { value: "village_accountant", labelKey: "designation.village_accountant" },
  { value: "revenue_inspector", labelKey: "designation.revenue_inspector" },
];

// Helper function to get circle label based on language
const getCircleLabel = (circle, currentLanguage) => {
  if (!circle) return '';
  if (currentLanguage === 'kn' && circle.label_kn) {
    return circle.label_kn;
  }
  return circle.label || circle.value || '';
};

export default function ApplicationsManagement({ user }) {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || 'kn';
  
  // Check if user is from Survey Department (Circle doesn't apply)
  const SURVEY_DESIGNATIONS = ['ddlr', 'adlr', 'surveyor', 'case_worker', 'superintendent', 'survey_supervisor'];
  const isSurveyUser = user?.department === 'survey' || SURVEY_DESIGNATIONS.includes(user?.designation);
  
  // State for unassigned applications
  const [unassignedApps, setUnassignedApps] = useState([]);
  const [unassignedTotal, setUnassignedTotal] = useState(0);
  const [unassignedPages, setUnassignedPages] = useState(1);
  const [loading, setLoading] = useState(true);
  
  // Location filters
  const [hobliFilter, setHobliFilter] = useState("all");
  const [circleFilter, setCircleFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  
  // Bulk assign states
  const [showBulkAssignDialog, setShowBulkAssignDialog] = useState(false);
  const [bulkAssignOfficial, setBulkAssignOfficial] = useState("");
  const [bulkAssignLoading, setBulkAssignLoading] = useState(false);
  const [bulkOfficialSearchQuery, setBulkOfficialSearchQuery] = useState("");
  const [debouncedBulkSearchQuery, setDebouncedBulkSearchQuery] = useState("");
  const [bulkAssignNote, setBulkAssignNote] = useState("");
  const [bulkAssignToStatus, setBulkAssignToStatus] = useState("");
  
  // Assignment dialog states
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedOfficial, setSelectedOfficial] = useState("");
  const [assignableOfficials, setAssignableOfficials] = useState([]);
  const [officialSearchQuery, setOfficialSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [assignNote, setAssignNote] = useState("");
  const [assignToStatus, setAssignToStatus] = useState("");
  const searchDebounceRef = useRef(null);

  // Get workflow statuses based on user's department (imported from AssignOfficialModal)
  const workflowStatuses = isSurveyUser ? SURVEY_WORKFLOW_STATUSES : REVENUE_WORKFLOW_STATUSES;

  // Debounce search query for better performance
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearchQuery(officialSearchQuery);
    }, 150); // 150ms debounce
    
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [officialSearchQuery]);

  // Debounce bulk search query
  const bulkSearchDebounceRef = useRef(null);
  useEffect(() => {
    if (bulkSearchDebounceRef.current) {
      clearTimeout(bulkSearchDebounceRef.current);
    }
    bulkSearchDebounceRef.current = setTimeout(() => {
      setDebouncedBulkSearchQuery(bulkOfficialSearchQuery);
    }, 150);
    
    return () => {
      if (bulkSearchDebounceRef.current) {
        clearTimeout(bulkSearchDebounceRef.current);
      }
    };
  }, [bulkOfficialSearchQuery]);
  
  // Get available circles based on selected hobli
  const availableCircles = useMemo(() => {
    if (hobliFilter === "all") return [];
    return CIRCLES_BY_HOBLI[hobliFilter] || [];
  }, [hobliFilter]);

  // Get officials eligible for bulk assignment
  const bulkAssignableOfficials = useMemo(() => {
    return assignableOfficials.filter(o => 
      BULK_ASSIGN_DESIGNATIONS.some(d => d.value === o.designation)
    );
  }, [assignableOfficials]);

  // Survey Office designations (work at Survey Office, not in the field)
  const SURVEY_OFFICE_DESIGNATIONS = ['adlr', 'case_worker', 'survey_supervisor', 'superintendent'];
  // Field Office designations (work in the field)
  const SURVEY_FIELD_DESIGNATIONS = ['surveyor', 'taluk_surveyor'];

  // Categorize bulk assignable officials into Taluk Office and Field Office
  // Using ALL assignable officials (same as regular Assign modal)
  // For Survey users, categorize by designation instead of field assignment
  // For Revenue users, filter Field Office by selected hobli/circle filter
  const { bulkTalukOfficeOfficials, bulkFieldOfficeOfficials } = useMemo(() => {
    const talukOffice = [];
    const fieldOffice = [];
    
    // Get selected hobli for filtering (convert to uppercase for comparison)
    const selectedHobliUpper = hobliFilter && hobliFilter !== 'all' ? hobliFilter.toUpperCase() : '';
    const selectedCircleUpper = circleFilter && circleFilter !== 'all' ? circleFilter.toUpperCase() : '';
    
    // Helper to normalize Kannada to English and uppercase
    const normalizeHobli = (hobli) => {
      if (!hobli) return '';
      const english = HOBLI_KANNADA_TO_ENGLISH[hobli] || hobli;
      return english.toUpperCase();
    };
    
    const normalizeCircle = (circle) => {
      if (!circle) return '';
      const english = CIRCLE_KANNADA_TO_ENGLISH[circle] || circle;
      return english.toUpperCase().replace(/[_\s]+/g, ''); // Remove underscores/spaces for comparison
    };
    
    // Normalize the selected filters
    const normalizedSelectedHobli = selectedHobliUpper;
    const normalizedSelectedCircle = selectedCircleUpper.replace(/[_\s]+/g, '');
    
    assignableOfficials.forEach(official => {
      if (isSurveyUser) {
        // For Survey users, ONLY show Survey department officials
        // Filter by department first, then categorize by designation
        if (official.department !== 'survey') {
          return; // Skip non-Survey officials
        }
        
        // Categorize by designation
        if (SURVEY_FIELD_DESIGNATIONS.includes(official.designation)) {
          fieldOffice.push(official);
        } else {
          // All other Survey officials go to Survey Office column
          talukOffice.push(official);
        }
      } else {
        // For Revenue users, ONLY show Revenue department officials
        if (official.department === 'survey') {
          return; // Skip Survey officials
        }
        
        // Categorize by field assignment
        const hasFieldAssignment = 
          (official.assigned_hobli && official.assigned_hobli !== '') ||
          (official.circle_assignments && official.circle_assignments.length > 0);
        
        if (hasFieldAssignment) {
          // Get official's hobli - either from assigned_hobli or from circle_assignments
          const officialHobli = normalizeHobli(official.assigned_hobli);
          
          // Get circles from circle_assignments (normalized)
          const officialCircles = (official.circle_assignments || []).map(ca => {
            if (typeof ca === 'string') return normalizeCircle(ca);
            if (ca && typeof ca === 'object' && ca.circle) return normalizeCircle(ca.circle);
            return '';
          }).filter(Boolean);
          
          // Get hoblis from circle_assignments (for Village Accountants who don't have assigned_hobli)
          const officialHoblisFromCircles = (official.circle_assignments || []).map(ca => {
            if (typeof ca === 'object' && ca.hobli) return normalizeHobli(ca.hobli);
            return '';
          }).filter(Boolean);
          
          // Check if official's hobli matches selected hobli filter
          // Match if: no filter, or direct hobli matches, or any circle_assignment hobli matches
          const hobliMatches = !normalizedSelectedHobli || 
            (officialHobli && officialHobli === normalizedSelectedHobli) ||
            officialHoblisFromCircles.includes(normalizedSelectedHobli);
          
          // Check if any of official's circles match selected circle filter
          const circleMatches = !normalizedSelectedCircle || officialCircles.length === 0 || officialCircles.includes(normalizedSelectedCircle);
          
          // Include if location matches the filter (or if no filter is applied)
          if (hobliMatches && circleMatches) {
            fieldOffice.push(official);
          }
        } else {
          talukOffice.push(official);
        }
      }
    });
    
    return { bulkTalukOfficeOfficials: talukOffice, bulkFieldOfficeOfficials: fieldOffice };
  }, [assignableOfficials, isSurveyUser, hobliFilter, circleFilter]);

  // Filter bulk officials by search query
  const filteredBulkTalukOfficials = useMemo(() => {
    if (!debouncedBulkSearchQuery.trim()) return bulkTalukOfficeOfficials;
    const query = debouncedBulkSearchQuery.toLowerCase();
    return bulkTalukOfficeOfficials.filter(official => {
      const nameMatch = official.name.toLowerCase().includes(query);
      const designationMatch = getDesignationLabel(official).toLowerCase().includes(query);
      const locationMatch = getSearchableLocation(official).includes(query);
      return nameMatch || designationMatch || locationMatch;
    });
  }, [bulkTalukOfficeOfficials, debouncedBulkSearchQuery, t]);

  const filteredBulkFieldOfficials = useMemo(() => {
    if (!debouncedBulkSearchQuery.trim()) return bulkFieldOfficeOfficials;
    const query = debouncedBulkSearchQuery.toLowerCase();
    return bulkFieldOfficeOfficials.filter(official => {
      const nameMatch = official.name.toLowerCase().includes(query);
      const designationMatch = getDesignationLabel(official).toLowerCase().includes(query);
      const locationMatch = getSearchableLocation(official).includes(query);
      return nameMatch || designationMatch || locationMatch;
    });
  }, [bulkFieldOfficeOfficials, debouncedBulkSearchQuery, t]);

  // getDesignationLabel imported from AssignOfficialModal

  // toEnglishLocation removed — getLocationInfo is now imported from AssignOfficialModal

  // getLocationInfo imported from AssignOfficialModal

  // Split officials into Taluk Office and Field Office
  // For Survey users, categorize by designation instead of field assignment
  // For Revenue users, filter Field Office by selected hobli/circle filter
  const { talukOfficeOfficials, fieldOfficeOfficials } = useMemo(() => {
    const talukOffice = [];
    const fieldOffice = [];
    
    // Get selected hobli for filtering (convert to uppercase for comparison)
    const selectedHobliUpper = hobliFilter && hobliFilter !== 'all' ? hobliFilter.toUpperCase() : '';
    const selectedCircleUpper = circleFilter && circleFilter !== 'all' ? circleFilter.toUpperCase() : '';
    
    // Helper to normalize Kannada to English and uppercase
    const normalizeHobli = (hobli) => {
      if (!hobli) return '';
      const english = HOBLI_KANNADA_TO_ENGLISH[hobli] || hobli;
      return english.toUpperCase();
    };
    
    const normalizeCircle = (circle) => {
      if (!circle) return '';
      const english = CIRCLE_KANNADA_TO_ENGLISH[circle] || circle;
      return english.toUpperCase().replace(/[_\s]+/g, ''); // Remove underscores/spaces for comparison
    };
    
    // Normalize the selected filters
    const normalizedSelectedHobli = selectedHobliUpper;
    const normalizedSelectedCircle = selectedCircleUpper.replace(/[_\s]+/g, '');
    
    assignableOfficials.forEach(official => {
      if (isSurveyUser) {
        // For Survey users, ONLY show Survey department officials
        // Filter by department first, then categorize by designation
        if (official.department !== 'survey') {
          return; // Skip non-Survey officials
        }
        
        // Categorize by designation
        if (SURVEY_FIELD_DESIGNATIONS.includes(official.designation)) {
          fieldOffice.push(official);
        } else {
          // All other Survey officials go to Survey Office column
          talukOffice.push(official);
        }
      } else {
        // For Revenue users, ONLY show Revenue department officials
        // Skip Survey department officials
        if (official.department === 'survey') {
          return; // Skip Survey officials for Revenue users
        }
        
        // Check if official is assigned to field (has hobli/circle assignments)
        const hasFieldAssignment = 
          (official.assigned_hobli && official.assigned_hobli !== '') ||
          (official.circle_assignments && official.circle_assignments.length > 0);
        
        // Field Office: officials WITH field assignments (hobli or circle)
        if (hasFieldAssignment) {
          // Get official's hobli - either from assigned_hobli or from circle_assignments (normalized)
          const officialHobli = normalizeHobli(official.assigned_hobli);
          
          // Get circles from circle_assignments (normalized)
          const officialCircles = (official.circle_assignments || []).map(ca => {
            if (typeof ca === 'string') return normalizeCircle(ca);
            if (ca && typeof ca === 'object' && ca.circle) return normalizeCircle(ca.circle);
            return '';
          }).filter(Boolean);
          
          // Get hoblis from circle_assignments (for Village Accountants who don't have assigned_hobli)
          const officialHoblisFromCircles = (official.circle_assignments || []).map(ca => {
            if (typeof ca === 'object' && ca.hobli) return normalizeHobli(ca.hobli);
            return '';
          }).filter(Boolean);
          
          // Check if official's hobli matches selected hobli filter
          // Match if: no filter, or direct hobli matches, or any circle_assignment hobli matches
          const hobliMatches = !normalizedSelectedHobli || 
            (officialHobli && officialHobli === normalizedSelectedHobli) ||
            officialHoblisFromCircles.includes(normalizedSelectedHobli);
          
          // Check if any of official's circles match selected circle filter
          const circleMatches = !normalizedSelectedCircle || officialCircles.length === 0 || officialCircles.includes(normalizedSelectedCircle);
          
          // Include if location matches the filter (or if no filter is applied)
          if (hobliMatches && circleMatches) {
            fieldOffice.push(official);
          }
        } else {
          // Taluk Office: officials WITHOUT field assignments
          talukOffice.push(official);
        }
      }
    });
    
    return { talukOfficeOfficials: talukOffice, fieldOfficeOfficials: fieldOffice };
  }, [assignableOfficials, isSurveyUser, hobliFilter, circleFilter]);

  // Helper function to get searchable location text (in English)
  const getSearchableLocation = (official) => {
    if (official.designation === 'revenue_inspector' && official.assigned_hobli) {
      const englishHobli = HOBLI_KANNADA_TO_ENGLISH[official.assigned_hobli] || official.assigned_hobli;
      return englishHobli.toLowerCase();
    }
    if (official.designation === 'village_accountant' && official.circle_assignments?.length > 0) {
      return official.circle_assignments
        .map(ca => {
          const circleValue = typeof ca === 'string' ? ca : (ca?.circle || '');
          return CIRCLE_KANNADA_TO_ENGLISH[circleValue] || circleValue;
        })
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
    }
    return '';
  };

  // Filter officials by search query (name, designation, and location) - using debounced query
  const filteredTalukOfficials = useMemo(() => {
    if (!debouncedSearchQuery.trim()) return talukOfficeOfficials;
    const query = debouncedSearchQuery.toLowerCase();
    return talukOfficeOfficials.filter(official => {
      const nameMatch = official.name.toLowerCase().includes(query);
      const designationMatch = getDesignationLabel(official).toLowerCase().includes(query);
      const locationMatch = getSearchableLocation(official).includes(query);
      return nameMatch || designationMatch || locationMatch;
    });
  }, [talukOfficeOfficials, debouncedSearchQuery, t]);

  const filteredFieldOfficials = useMemo(() => {
    if (!debouncedSearchQuery.trim()) return fieldOfficeOfficials;
    const query = debouncedSearchQuery.toLowerCase();
    return fieldOfficeOfficials.filter(official => {
      const nameMatch = official.name.toLowerCase().includes(query);
      const designationMatch = getDesignationLabel(official).toLowerCase().includes(query);
      const locationMatch = getSearchableLocation(official).includes(query);
      return nameMatch || designationMatch || locationMatch;
    });
  }, [fieldOfficeOfficials, debouncedSearchQuery, t]);

  // Officials filtered by SELECTED APPLICATION's location (for Individual Assign modal)
  // This is different from the page filters - it filters based on the specific application being assigned
  const { appFilteredTalukOfficials, appFilteredFieldOfficials } = useMemo(() => {
    if (!selectedApplication) {
      return { appFilteredTalukOfficials: talukOfficeOfficials, appFilteredFieldOfficials: fieldOfficeOfficials };
    }
    
    const talukOffice = [];
    const fieldOffice = [];
    
    // Get application location for filtering
    const appHobli = selectedApplication?.hobli?.toUpperCase() || '';
    const appCircle = selectedApplication?.circle?.toUpperCase() || '';
    const appVillage = selectedApplication?.village?.toUpperCase() || '';
    
    assignableOfficials.forEach(official => {
      if (isSurveyUser) {
        // For Survey users, ONLY show Survey department officials
        if (official.department !== 'survey') return;
        
        // Categorize by designation
        if (SURVEY_FIELD_DESIGNATIONS.includes(official.designation)) {
          fieldOffice.push(official);
        } else {
          talukOffice.push(official);
        }
      } else {
        // For Revenue users, ONLY show Revenue department officials
        // Skip Survey department officials
        if (official.department === 'survey') return;
        
        // Categorize by field assignment
        const hasFieldAssignment = 
          (official.assigned_hobli && official.assigned_hobli !== '') ||
          (official.circle_assignments && official.circle_assignments.length > 0);
        
        if (hasFieldAssignment) {
          // Only include Field Officials whose assigned location matches the application's location
          const officialHobli = official.assigned_hobli?.toUpperCase() || '';
          const officialCircles = (official.circle_assignments || []).map(ca => {
            if (typeof ca === 'string') return ca.toUpperCase();
            if (ca && typeof ca === 'object' && ca.circle) return ca.circle.toUpperCase();
            return '';
          }).filter(Boolean);
          
          // Check if official's hobli matches application's hobli
          const hobliMatches = !appHobli || !officialHobli || officialHobli === appHobli;
          
          // Check if any of official's circles match application's circle
          const circleMatches = !appCircle || officialCircles.length === 0 || officialCircles.includes(appCircle);
          
          // Include if location matches (or if application has no location data)
          if (hobliMatches && circleMatches) {
            fieldOffice.push(official);
          }
        } else {
          talukOffice.push(official);
        }
      }
    });
    
    return { appFilteredTalukOfficials: talukOffice, appFilteredFieldOfficials: fieldOffice };
  }, [assignableOfficials, isSurveyUser, selectedApplication, talukOfficeOfficials, fieldOfficeOfficials]);

  // Search-filtered officials for Individual Assign modal (filtered by selected application's location)
  const searchFilteredAppTalukOfficials = useMemo(() => {
    if (!debouncedSearchQuery.trim()) return appFilteredTalukOfficials;
    const query = debouncedSearchQuery.toLowerCase();
    return appFilteredTalukOfficials.filter(official => {
      const nameMatch = official.name.toLowerCase().includes(query);
      const designationMatch = getDesignationLabel(official).toLowerCase().includes(query);
      const locationMatch = getSearchableLocation(official).includes(query);
      return nameMatch || designationMatch || locationMatch;
    });
  }, [appFilteredTalukOfficials, debouncedSearchQuery, t]);

  const searchFilteredAppFieldOfficials = useMemo(() => {
    if (!debouncedSearchQuery.trim()) return appFilteredFieldOfficials;
    const query = debouncedSearchQuery.toLowerCase();
    return appFilteredFieldOfficials.filter(official => {
      const nameMatch = official.name.toLowerCase().includes(query);
      const designationMatch = getDesignationLabel(official).toLowerCase().includes(query);
      const locationMatch = getSearchableLocation(official).includes(query);
      return nameMatch || designationMatch || locationMatch;
    });
  }, [appFilteredFieldOfficials, debouncedSearchQuery, t]);

  // Fetch assignable officials
  const fetchAssignableOfficials = useCallback(async () => {
    try {
      const response = await api.get("/officials/assignable");
      setAssignableOfficials(response.data || []);
    } catch (error) {
      console.error("Error fetching assignable officials:", error);
    }
  }, []);

  // Fetch unassigned applications
  const fetchUnassignedApplications = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);
      if (hobliFilter && hobliFilter !== "all") {
        params.append("hobli", hobliFilter);
      }
      if (circleFilter && circleFilter !== "all") {
        params.append("circle", circleFilter);
      }
      const response = await api.get(`/officials/unassigned-applications?${params.toString()}`);
      setUnassignedApps(response.data.applications || []);
      setUnassignedTotal(response.data.total || 0);
      setUnassignedPages(response.data.pages || 1);
    } catch (error) {
      console.error("Error fetching unassigned applications:", error);
    } finally {
      setLoading(false);
    }
  }, [page, limit, hobliFilter, circleFilter]);

  // Handle hobli filter change - reset circle and page
  const handleHobliFilterChange = (value) => {
    setHobliFilter(value);
    setCircleFilter("all");
    setPage(1);
  };

  // Handle circle filter change - reset page
  const handleCircleFilterChange = (value) => {
    setCircleFilter(value);
    setPage(1);
  };

  // Open assign dialog
  const openAssignDialog = (app) => {
    setSelectedApplication(app);
    setSelectedOfficial("");
    setShowAssignDialog(true);
  };

  // Handle individual assignment
  const handleAssignApplication = async () => {
    if (!selectedApplication || !selectedOfficial) return;
    
    try {
      const payload = {
        official_id: selectedOfficial,
        assign_note: assignNote?.trim() || null,
        assign_to_status: assignToStatus || null
      };
      
      await api.put(`/applications/${selectedApplication.id}/assign`, payload);
      toast.success(t('officialsManagement.assignedSuccessfully'));
      setShowAssignDialog(false);
      setSelectedApplication(null);
      setSelectedOfficial("");
      setAssignNote("");
      setAssignToStatus("");
      fetchUnassignedApplications();
    } catch (error) {
      console.error("Assign error:", error);
      toast.error(error.response?.data?.detail || t('officialsManagement.errorAssigning'));
    }
  };

  // Bulk assign handler
  const handleBulkAssign = async () => {
    // For Survey users, check hobli filter; for Revenue users, check circle filter
    if (isSurveyUser) {
      if (!hobliFilter || hobliFilter === "all" || !bulkAssignOfficial) {
        toast.error(t('officialsManagement.selectHobliAndOfficial') || 'Please select a Hobli and an official');
        return;
      }
    } else {
      if (!circleFilter || circleFilter === "all" || !bulkAssignOfficial) {
        toast.error(t('officialsManagement.selectCircleAndOfficial'));
        return;
      }
    }
    
    setBulkAssignLoading(true);
    try {
      const params = new URLSearchParams();
      if (!isSurveyUser && circleFilter && circleFilter !== "all") {
        params.append("circle", circleFilter);
      }
      params.append("official_id", bulkAssignOfficial);
      if (hobliFilter && hobliFilter !== "all") {
        params.append("hobli", hobliFilter);
      }
      if (bulkAssignNote?.trim()) {
        params.append("assign_note", bulkAssignNote.trim());
      }
      if (bulkAssignToStatus) {
        params.append("assign_to_status", bulkAssignToStatus);
      }
      
      const response = await api.post(`/officials/bulk-assign-by-circle?${params.toString()}`);
      toast.success(t('officialsManagement.bulkAssignSuccess', { count: response.data.assigned_count, name: response.data.official_name }));
      setShowBulkAssignDialog(false);
      setBulkAssignOfficial("");
      setBulkAssignNote("");
      setBulkAssignToStatus("");
      fetchUnassignedApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('officialsManagement.bulkAssignError'));
    } finally {
      setBulkAssignLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchAssignableOfficials();
  }, [fetchAssignableOfficials]);

  useEffect(() => {
    fetchUnassignedApplications();
  }, [fetchUnassignedApplications]);

  // Loading state
  if (loading && unassignedApps.length === 0) {
    return <LoadingOverlay fullPage={false} />;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <FileText className="w-6 h-6 text-amber-600" />
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            {t('applicationsManagement.title') || 'ಅರ್ಜಿ ನಿರ್ವಹಣೆ'}
          </h2>
          <p className="text-sm text-slate-500">
            {t('applicationsManagement.subtitleUnassigned')}
          </p>
        </div>
      </div>

      {/* Location Filters */}
      <Card className="p-4 bg-white border border-stone-200">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-slate-500" />
            <Select value={hobliFilter} onValueChange={handleHobliFilterChange}>
              <SelectTrigger className="w-[180px]" data-testid="apps-hobli-filter">
                <SelectValue placeholder={t('filter.selectHobli')} />
              </SelectTrigger>
              <SelectContent>
                {HOBLIS.map((h) => (
                  <SelectItem key={h.value} value={h.value}>
                    {t(h.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Circle filter - Hidden for Survey users */}
          {!isSurveyUser && hobliFilter !== "all" && availableCircles.length > 0 && (
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <Select value={circleFilter} onValueChange={handleCircleFilterChange}>
                <SelectTrigger className="w-[200px]" data-testid="apps-circle-filter">
                  <SelectValue placeholder={t('filter.selectCircle')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('circle.allCircles')}</SelectItem>
                  {availableCircles.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {getCircleLabel(c, currentLanguage)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Unassigned Count Badge - next to filters */}
          <Badge className="bg-amber-100 text-amber-800 text-base px-3 py-1">
            {unassignedTotal} {t('applicationsManagement.unassigned') || 'ನಿಯೋಜಿಸದ'}
          </Badge>

          {/* Selected Hobli/Circle Badge - before Clear Filter */}
          {hobliFilter !== "all" && (
            <Badge variant="outline" className="text-xs">
              {hobliFilter}
              {!isSurveyUser && circleFilter !== "all" && ` - ${getCircleLabel(availableCircles.find(c => c.value === circleFilter) || {label: circleFilter}, currentLanguage)}`}
            </Badge>
          )}

          {/* Clear Filter Button - show only when filters are applied */}
          {(hobliFilter !== "all" || circleFilter !== "all") && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setHobliFilter("all");
                setCircleFilter("all");
                setPage(1);
              }}
              className="text-slate-500 hover:text-slate-700 hover:bg-slate-100"
              data-testid="clear-filters-btn"
            >
              <X className="w-4 h-4 mr-1" />
              {t('locationReports.clearFilters') || 'ಫಿಲ್ಟರ್ ತೆರವುಗೊಳಿಸಿ'}
            </Button>
          )}

          {/* Bulk Assign Button
              - For Survey users: show when Hobli is selected
              - For Revenue users: show when Circle is selected
          */}
          {((isSurveyUser && hobliFilter !== "all") || (!isSurveyUser && circleFilter !== "all")) && unassignedTotal > 0 && (
            <Button
              onClick={() => setShowBulkAssignDialog(true)}
              className="bg-amber-600 hover:bg-amber-700 text-white ml-auto"
              data-testid="bulk-assign-btn"
            >
              <UserCheck className="w-4 h-4 mr-2" />
              {isSurveyUser 
                ? (t('officialsManagement.bulkAssignHobli') || 'ಹೋಬಳಿಯ ಎಲ್ಲಾ ನಿಯೋಜಿಸಿ')
                : t('officialsManagement.bulkAssignCircle')
              } ({unassignedTotal})
            </Button>
          )}
        </div>
      </Card>

      {/* Results count and pagination */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-2">
        {/* Results count */}
        <div className="text-xs md:text-sm text-slate-600">
          {t('pagination.showing')} {Math.min((page - 1) * limit + 1, unassignedTotal)} - {Math.min(page * limit, unassignedTotal)} {t('pagination.of')} {unassignedTotal} {t('pagination.applications')}
        </div>
        
        {/* Top Pagination Controls */}
        {unassignedPages > 1 && (
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-1.5 md:px-2 h-7 md:h-8"
              data-testid="apps-prev-btn-top"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              <span className="hidden md:inline ml-1">{t('pagination.previous')}</span>
            </Button>
            
            <div className="flex items-center gap-0.5 md:gap-1">
              {Array.from({ length: Math.min(window.innerWidth < 768 ? 3 : 5, unassignedPages) }, (_, i) => {
                let pageNum;
                const maxV = window.innerWidth < 768 ? 3 : 5;
                if (unassignedPages <= maxV) {
                  pageNum = i + 1;
                } else if (page <= Math.ceil(maxV / 2)) {
                  pageNum = i + 1;
                } else if (page >= unassignedPages - Math.floor(maxV / 2)) {
                  pageNum = unassignedPages - maxV + 1 + i;
                } else {
                  pageNum = page - Math.floor(maxV / 2) + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={page === pageNum ? "default" : "outline"}
                    size="sm"
                    className={`w-7 h-7 md:w-8 md:h-8 text-xs ${page === pageNum ? "bg-amber-600" : ""}`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(unassignedPages, p + 1))}
              disabled={page === unassignedPages}
              className="px-1.5 md:px-2 h-7 md:h-8"
              data-testid="apps-next-btn-top"
            >
              <span className="hidden md:inline mr-1">{t('pagination.next')}</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </div>

      {/* Applications List */}
      {unassignedTotal === 0 ? (
        <Card className="p-8 text-center bg-stone-50">
          <CheckCircle className="w-12 h-12 text-emerald-300 mx-auto mb-4" />
          <p className="text-slate-500">{t('officialsManagement.noUnassigned')}</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {unassignedApps.map((app, index) => (
            <Card key={app.id} className="p-3 md:p-4 bg-white border-l-[6px] border-l-amber-500 border-2 border-amber-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden" data-testid={`unassigned-app-${app.id}`}>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-6">
                {/* Top row on mobile: Serial + Survey + Status */}
                <div className="flex items-center gap-3">
                  {/* Serial Number */}
                  <span className="text-xs font-semibold text-slate-400 bg-slate-100 rounded-full w-6 h-6 md:w-7 md:h-7 flex items-center justify-center flex-shrink-0">
                    {(page - 1) * limit + index + 1}
                  </span>
                  
                  {/* Survey Number & Status */}
                  <div className="flex flex-col gap-1">
                    <div>
                      <span className="text-xs text-slate-600 font-bold">{currentLanguage === 'kn' ? 'ಸ.ನಂ.' : 'Sy. No.'}</span>
                      <span className="font-bold text-slate-900 text-sm md:text-base ml-1">{app.land_survey_number}</span>
                    </div>
                    <Badge className={`text-xs w-fit ${app.status === 'submitted' ? 'bg-emerald-700 text-white' : 'bg-yellow-100 text-yellow-800'}`}>
                      {t(`status.${app.status}`)}
                    </Badge>
                  </div>
                </div>

                {/* Farmer Details */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{app.farmer_name}</p>
                  <p className="text-xs text-slate-500 truncate">{app.village}, {app.hobli}, {app.taluk}</p>
                  {app.circle && (
                    <Badge variant="outline" className="text-xs mt-1">
                      {t(`circle.${app.circle}`) !== `circle.${app.circle}` ? t(`circle.${app.circle}`) : app.circle}
                    </Badge>
                  )}
                </div>

                {/* Purpose + Date row on mobile */}
                <div className="flex items-end justify-between gap-3 md:contents">
                  {/* Purpose Section */}
                  <div className="flex-1 min-w-0">
                    {app.purpose && (
                      <div className="text-xs">
                        <span className="text-slate-500 font-bold block">{currentLanguage === 'kn' ? 'ಉದ್ದೇಶ' : 'Purpose'}</span>
                        <span className="text-slate-700 line-clamp-2">{t(`purpose.${app.purpose}`) !== `purpose.${app.purpose}` ? t(`purpose.${app.purpose}`) : app.purpose}</span>
                      </div>
                    )}
                  </div>

                  {/* Date Section */}
                  <div className="text-right flex-shrink-0">
                    {app.created_at && (
                      <div className="text-xs">
                        <span className="text-slate-500 font-bold flex items-center justify-end gap-1">
                          <Calendar className="w-3 h-3" />
                          <span className="hidden md:inline">{currentLanguage === 'kn' ? 'ಸಲ್ಲಿಸಿದ ದಿನ' : 'Submitted On'}</span>
                        </span>
                        <span className="text-slate-600 font-medium">
                          {new Date(app.created_at).toLocaleDateString(currentLanguage === 'kn' ? 'kn-IN' : 'en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Button */}
                <Button
                  onClick={() => openAssignDialog(app)}
                  className="bg-emerald-800 hover:bg-emerald-900 text-white shrink-0 w-full md:w-auto text-sm"
                  data-testid="assign-app-btn"
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  {t('officialsManagement.assignToOfficial')}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Bottom Pagination Controls */}
      {unassignedPages > 1 && (
        <div className="flex items-center justify-center gap-1 mt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-1.5 md:px-2 h-7 md:h-8"
            data-testid="apps-prev-btn"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            <span className="hidden md:inline ml-1">{t('pagination.previous')}</span>
          </Button>
          
          <div className="flex items-center gap-0.5 md:gap-1">
            {Array.from({ length: Math.min(window.innerWidth < 768 ? 3 : 5, unassignedPages) }, (_, i) => {
              let pageNum;
              const maxV = window.innerWidth < 768 ? 3 : 5;
              if (unassignedPages <= maxV) {
                pageNum = i + 1;
              } else if (page <= Math.ceil(maxV / 2)) {
                pageNum = i + 1;
              } else if (page >= unassignedPages - Math.floor(maxV / 2)) {
                pageNum = unassignedPages - maxV + 1 + i;
              } else {
                pageNum = page - Math.floor(maxV / 2) + i;
              }
              return (
                <Button
                  key={pageNum}
                  variant={page === pageNum ? "default" : "outline"}
                  size="sm"
                  className={`w-7 h-7 md:w-8 md:h-8 text-xs ${page === pageNum ? "bg-amber-600" : ""}`}
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(unassignedPages, p + 1))}
            disabled={page === unassignedPages}
            className="px-1.5 md:px-2 h-7 md:h-8"
            data-testid="apps-next-btn"
          >
            <span className="hidden md:inline mr-1">{t('pagination.next')}</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}

      {/* Bulk Assign Dialog */}
      <AssignOfficialModal
        open={showBulkAssignDialog}
        onOpenChange={(open) => {
          setShowBulkAssignDialog(open);
          if (!open) {
            setBulkOfficialSearchQuery("");
            setDebouncedBulkSearchQuery("");
            setBulkAssignOfficial("");
          }
        }}
        title={t('officialsManagement.bulkAssignTitle')}
        description={t('officialsManagement.bulkAssignDescription', { 
          count: unassignedTotal,
          circle: getCircleLabel(availableCircles.find(c => c.value === circleFilter) || {label: circleFilter}, currentLanguage)
        })}
        officeOfficials={filteredBulkTalukOfficials}
        fieldOfficials={filteredBulkFieldOfficials}
        allOfficials={assignableOfficials}
        selectedOfficialId={bulkAssignOfficial}
        onSelectOfficial={setBulkAssignOfficial}
        searchQuery={bulkOfficialSearchQuery}
        onSearchChange={setBulkOfficialSearchQuery}
        assignNote={bulkAssignNote}
        onAssignNoteChange={setBulkAssignNote}
        assignToStatus={bulkAssignToStatus}
        onAssignToStatusChange={setBulkAssignToStatus}
        workflowStatuses={workflowStatuses}
        isSurveyUser={isSurveyUser}
        loading={bulkAssignLoading}
        onConfirm={handleBulkAssign}
        onCancel={() => {
          setShowBulkAssignDialog(false);
          setBulkOfficialSearchQuery("");
          setDebouncedBulkSearchQuery("");
          setBulkAssignOfficial("");
          setBulkAssignNote("");
          setBulkAssignToStatus("");
        }}
        confirmLabel={t('officialsManagement.confirmBulkAssign')}
        confirmClassName="bg-amber-600 hover:bg-amber-700"
        testIdPrefix="bulk-assign"
        scrollHeight="h-[200px]"
      />

      {/* Individual Assign Dialog */}
      <AssignOfficialModal
        open={showAssignDialog}
        onOpenChange={(open) => {
          setShowAssignDialog(open);
          if (!open) {
            setOfficialSearchQuery("");
            setDebouncedSearchQuery("");
            setSelectedOfficial("");
          }
        }}
        title={t('officialsManagement.assignApplication')}
        description={
          selectedApplication
            ? `${t('officialsManagement.assignApplicationDesc')} #${selectedApplication.land_survey_number}${
                selectedApplication.hobli
                  ? ` (${selectedApplication.hobli}${selectedApplication.circle ? ` / ${selectedApplication.circle}` : ''}${selectedApplication.village ? ` / ${selectedApplication.village}` : ''})`
                  : ''
              }`
            : undefined
        }
        officeOfficials={searchFilteredAppTalukOfficials}
        fieldOfficials={searchFilteredAppFieldOfficials}
        allOfficials={assignableOfficials}
        selectedOfficialId={selectedOfficial}
        onSelectOfficial={setSelectedOfficial}
        searchQuery={officialSearchQuery}
        onSearchChange={setOfficialSearchQuery}
        assignNote={assignNote}
        onAssignNoteChange={setAssignNote}
        assignToStatus={assignToStatus}
        onAssignToStatusChange={setAssignToStatus}
        workflowStatuses={workflowStatuses}
        isSurveyUser={isSurveyUser}
        onConfirm={handleAssignApplication}
        onCancel={() => {
          setShowAssignDialog(false);
          setOfficialSearchQuery("");
          setDebouncedSearchQuery("");
          setSelectedOfficial("");
          setAssignNote("");
          setAssignToStatus("");
        }}
        confirmLabel={t('officialsManagement.confirmAssign')}
        confirmClassName="bg-emerald-700 hover:bg-emerald-800"
        testIdPrefix="individual-assign"
        emptyFieldMessage={
          !isSurveyUser && selectedApplication?.hobli
            ? (t('officialsManagement.noFieldOfficialsForLocation') || `No field officials found for ${selectedApplication.hobli}`)
            : undefined
        }
      />
    </div>
  );
}
